import { Fragment, FunctionComponent, useEffect, useState } from "react";
import { useOrderCreateCtx } from "../../../../../context_providers/order_create_context";
import { OrderItemModelV2 } from "../../../../../service/models/orders/order_item_model";
import { GetSubTotalAmt } from "../../../../../utils/order_util";
import ProductRowWidgetV2 from "./product_rowV2";
import { ItemModelV2 } from "../../../../../erp/inventory/models/item_model";
import RawMaterialRowWidget from "./product_raw_material_widget";
import {
  ToastType,
  useToast,
} from "../../../../../context_providers/toast/toast_ctx";
import OrgSearchField from "../../order_detail/subscription/org/org_search";
import { fetchPost } from "../../../../../service/api_client";
import { mdiDownload, mdiUpload } from "@mdi/js";
import Icon from "@mdi/react";
import ItemBulkUpload from "./ItemBulkUpload";
import {
  ItemCategory,
  buildValidateRow,
  downloadSampleTemplate,
} from "./bulk_item_utils";

interface ProductSectionProps {}

// Serialized Finished Goods belong only in the Products/FG section. Non-Serialized
// Finished Goods stay searchable here: the Products row only accepts "Finished Goods",
// so excluding them too left those items addable nowhere.
const FINISHED_GOODS_SUBCATEGORIES = ["Finished Goods"];

type AvailabilityRow = {
  warehouse_id: string;
  warehouse_name?: string;
  available_units: number;
};

const FIXED_WAREHOUSE_IDS = [
  "WRH-938723",
  "WRH-981795",
  "WRH-028178",
  "WRH-144909",
  "WRH-885247", // Kazam Begur Warehouse
];

const ProductSection: FunctionComponent<ProductSectionProps> = () => {
  const { order, setOrder } = useOrderCreateCtx();
  const [formId, setFormId] = useState(0);
  const { showToast } = useToast();

  const calculateTotalSubtotal = ({
    products,
    services,
    raw_materials,
  }: {
    products: OrderItemModelV2[];
    services: OrderItemModelV2[];
    raw_materials: OrderItemModelV2[];
  }) => {
    return (
      GetSubTotalAmt(products) +
      GetSubTotalAmt(services) +
      GetSubTotalAmt(raw_materials)
    );
  };

  const [pendingIsFinishedGoods, setPendingIsFinishedGoods] = useState(false);
  const hasFinishedGoods =
    pendingIsFinishedGoods ||
    (order.products ?? []).some(
      (p) => p?.item?.subcategory === "Finished Goods",
    );

  const [pendingItemCode, setPendingItemCode] = useState<string>("");
  const [pendingRmItemCode, setPendingRmItemCode] = useState<string>("");

  const [bulkModal, setBulkModal] = useState<ItemCategory | null>(null);

  // Per-item availability map: item_code → AvailabilityRow[]
  const [availabilityMap, setAvailabilityMap] = useState<
    Record<string, { loading: boolean; data: AvailabilityRow[] }>
  >({});

  const fetchAvailabilityForItem = async (item: ItemModelV2) => {
    if (!item?.item_code) return;
    const code = item.item_code;

    setAvailabilityMap((prev) => ({
      ...prev,
      [code]: { loading: true, data: prev[code]?.data ?? [] },
    }));

    try {
      const url = `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/erp/inventory/items/warehouses/availability`;
      const res = await fetchPost(url, {
        warehouse_ids: FIXED_WAREHOUSE_IDS,
        item_code: code,
      });
      if (res.success) {
        setAvailabilityMap((prev) => ({
          ...prev,
          [code]: {
            loading: false,
            data: (res.result ?? []).map((r: any) => ({
              warehouse_id: r.warehouse_id,
              warehouse_name: r.warehouse_name,
              available_units: r.available_units,
            })),
          },
        }));
      }
    } catch {
      setAvailabilityMap((prev) => ({
        ...prev,
        [code]: { loading: false, data: [] },
      }));
    }
  };

  const makeValidateRow = (section: ItemCategory) =>
    buildValidateRow(section, () =>
      section === "product"
        ? order.products
        : section === "service"
          ? order.services
          : (order.raw_materials ?? []),
    );

  const handleBulkComplete = (
    section: ItemCategory,
    items: OrderItemModelV2[],
  ) => {
    if (items.length === 0) return;
    const withIds = items.map((it, idx) => ({ ...it, id: Date.now() + idx }));
    setOrder((old) => {
      if (section === "product") {
        const products = [...old.products, ...withIds];
        return {
          ...old,
          products,
          subtotal: calculateTotalSubtotal({
            products,
            services: old.services,
            raw_materials: old.raw_materials,
          }),
        };
      }
      if (section === "service") {
        const services = [...old.services, ...withIds];
        return {
          ...old,
          services,
          subtotal: calculateTotalSubtotal({
            products: old.products,
            services,
            raw_materials: old.raw_materials,
          }),
        };
      }
      const raw_materials = [...(old.raw_materials ?? []), ...withIds];
      return {
        ...old,
        raw_materials,
        subtotal: calculateTotalSubtotal({
          products: old.products,
          services: old.services,
          raw_materials,
        }),
      };
    });
    if (section === "product" || section === "raw") {
      withIds.forEach((it) => {
        if (it.item?.item_code) fetchAvailabilityForItem(it.item);
      });
    }
  };

  const bulkModalTitle =
    bulkModal === "product"
      ? "Bulk Upload Products"
      : bulkModal === "service"
        ? "Bulk Upload Services"
        : "Bulk Upload Raw Materials";

  const renderSectionHeader = (label: string, section: ItemCategory) => (
    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 mt-5">
      <h3 className="font-semibold">{label}</h3>
      <div className="flex items-center gap-2">
        <button
          id={`order-create-${section}-download-template-btn`}
          type="button"
          onClick={downloadSampleTemplate}
          className="flex items-center gap-1 text-xs md:text-sm border border-myPrimaryColor text-myPrimaryColor px-3 py-1.5 rounded hover:bg-green-50 transition-colors"
        >
          <Icon path={mdiDownload} className="h-4 w-4" />
          Download Sample Template
        </button>
        <button
          id={`order-create-${section}-upload-csv-btn`}
          type="button"
          onClick={() => setBulkModal(section)}
          className="flex items-center gap-1 text-xs md:text-sm bg-myPrimaryColor text-white px-3 py-1.5 rounded hover:bg-green-700 transition-colors"
        >
          <Icon path={mdiUpload} className="h-4 w-4" />
          Upload CSV
        </button>
      </div>
    </div>
  );

  // Fetch availability for all added products on mount
  useEffect(() => {
    for (const p of order.products ?? []) {
      if (p?.item?.item_code && !availabilityMap[p.item.item_code]) {
        fetchAvailabilityForItem(p.item);
      }
    }
  }, [order.products.length]);

  const renderAvailability = (itemCode?: string) => {
    if (!itemCode) return null;
    const entry = availabilityMap[itemCode];
    if (!entry) return null;
    return (
      <div className="mt-3 pt-3 border-t border-gray-100 text-xs flex flex-wrap items-center gap-x-4 gap-y-1">
        <span className="font-semibold text-gray-700">
          Available ({itemCode}):
        </span>
        {entry.loading ? (
          <span className="text-gray-500">Loading…</span>
        ) : entry.data.length === 0 ? (
          <span className="text-gray-500">No data</span>
        ) : (
          entry.data.map((row) => (
            <span key={row.warehouse_id} className="whitespace-nowrap">
              <span className="text-gray-500">
                {row.warehouse_name ?? row.warehouse_id}:
              </span>{" "}
              <span className="font-semibold">{row.available_units}</span>
            </span>
          ))
        )}
      </div>
    );
  };

  return (
    <>
      {/* Product Section */}
      <section id="order-create-products-section" className="overflow-x-auto">
        {renderSectionHeader("Products", "product")}
        <div className="hidden md:block bg-white rounded-md shadow p-3 my-2 text-sm">
          <div className="grid grid-cols-9 gap-3 ">
            <h4 className="font-semibold text-center">
              <span className="text-base text-red-500">*</span> Item Code
            </h4>
            <h4 className="font-semibold text-center">
              <span className="text-base text-red-500">*</span> Product/Service
            </h4>
            <h4 className="font-semibold text-center">
              <span className="text-base text-red-500">*</span> Description
            </h4>
            <h4 className="font-semibold text-center">
              <span className="text-base text-red-500">*</span> Quantity
            </h4>
            <h4 className="font-semibold text-center">
              Price per unit (excl. GST)
            </h4>
            <h4 className="font-semibold text-center">GST %</h4>
            <h4 className="font-semibold text-center">
              Final Price (incl. GST)
            </h4>
            <h4 className="font-semibold text-center">Amount</h4>
            <h4 className="font-semibold text-center">Action</h4>
          </div>
        </div>

        <div className="flex flex-col gap-2 text-sm">
          {order.products.map((item) => (
            <Fragment key={item.item_id}>
              <ProductRowWidgetV2
                id_prefix="order-create-product"
                item_search_type="Product"
                item_search_subcategory="Finished Goods"
                edit={false}
                item={item}
                availability={renderAvailability(item?.item?.item_code)}
                onDelete={(it: OrderItemModelV2) => {
                  setOrder((old) => {
                    const products = old.products.filter(
                      (v) => v.item_id !== it.item_id,
                    );
                    return {
                      ...old,
                      products,
                      subtotal: calculateTotalSubtotal({
                        products,
                        services: order.services,
                        raw_materials: order.raw_materials,
                      }),
                    };
                  });
                }}
                onSubmit={(data) => {
                  setOrder((old) => {
                    const products = old.products.map((it) =>
                      it.id === item.id ? data : it,
                    );
                    return {
                      ...old,
                      products,
                      subtotal: calculateTotalSubtotal({
                        products,
                        services: order.services,
                        raw_materials: order.raw_materials,
                      }),
                    };
                  });
                  setFormId(Math.random());
                }}
              />
            </Fragment>
          ))}

          <ProductRowWidgetV2
            key={formId}
            id_prefix="order-create-product"
            edit={true}
            item_search_type={"Product"}
            item_search_subcategory="Finished Goods"
            availability={
              pendingItemCode ? renderAvailability(pendingItemCode) : null
            }
            onItemChange={(it) => {
              setPendingIsFinishedGoods(it?.subcategory === "Finished Goods");
              setPendingItemCode(it?.item_code ?? "");
              fetchAvailabilityForItem(it);
            }}
            onSubmit={(data) => {
              setOrder((old) => {
                const alreadyExists = old.products.some(
                  (it) => it.item_id === data.item_id,
                );
                if (alreadyExists) {
                  showToast({
                    type: ToastType.error,
                    text: "Item already exists",
                  });
                  return old;
                }

                const products = [...old.products, data];

                return {
                  ...old,
                  products,
                  subtotal: calculateTotalSubtotal({
                    products,
                    services: old.services,
                    raw_materials: old.raw_materials,
                  }),
                };
              });
              // Fetch availability for newly added item
              if (data?.item?.item_code) {
                fetchAvailabilityForItem(data.item);
              }
              setPendingIsFinishedGoods(false);
              setPendingItemCode("");
              setFormId(Math.random());
            }}
          />
          <div className="flex flex-col md:flex-row md:justify-between gap-1">
            <p className="text-xs md:text-sm italic text-gray-400">
              Please select product and qty then hit + to add product.
            </p>
            <p className="text-xs md:text-sm italic text-gray-400">
              Note: Enter the base price (excluding GST) — GST is applied
              automatically from the item's tax slab, and the final
              GST-inclusive price is what's used for the order.
            </p>
          </div>
        </div>
      </section>

      {/* Organization — only when any Finished Goods product is present */}
      {hasFinishedGoods && (
        <section id="order-create-org-section" className="overflow-x-auto">
          <h3 className="font-semibold mt-5 text-base">Organization</h3>
          <div className="bg-white rounded-md shadow p-2 md:p-6 my-2">
            <label
              htmlFor="order-create-org-search"
              className="font-semibold block mb-2 text-sm"
            >
              <span className="text-red-500">*</span> Organization
            </label>
            <div className="bg-gray-100 rounded">
              <OrgSearchField
                id="order-create-org-search"
                value={(order as any).org}
                placeholder="Search and select an Organization"
                onSelect={(o) =>
                  setOrder((old) => ({
                    ...(old as any),
                    org: { name: o.name, namespace: o.namespace },
                  }))
                }
              />
            </div>
          </div>
        </section>
      )}

      {/* Service Section */}
      <section id="order-create-services-section" className="overflow-x-auto">
        {renderSectionHeader("Services", "service")}
        <div className="hidden md:block bg-white rounded-md shadow p-3 my-2 text-sm">
          <div className="grid grid-cols-9 gap-3 ">
            <h4 className="font-semibold text-center">
              <span className="text-base text-red-500">*</span> Item Code
            </h4>
            <h4 className="font-semibold text-center">
              <span className="text-base text-red-500">*</span> Product/Service
            </h4>
            <h4 className="font-semibold text-center">
              <span className="text-base text-red-500">*</span> Description
            </h4>
            <h4 className="font-semibold text-center">
              <span className="text-base text-red-500">*</span> Quantity
            </h4>
            <h4 className="font-semibold text-center">
              Price per unit (excl. GST)
            </h4>
            <h4 className="font-semibold text-center">GST %</h4>
            <h4 className="font-semibold text-center">
              Final Price (incl. GST)
            </h4>
            <h4 className="font-semibold text-center">Amount</h4>
            <h4 className="font-semibold text-center">Action</h4>
          </div>
        </div>
        <div className="flex flex-col gap-2 text-sm">
          {order.services.map((item) => (
            <ProductRowWidgetV2
              id_prefix="order-create-service"
              item_search_type="Service"
              edit={false}
              key={item.item_id}
              item={item}
              onDelete={(it: OrderItemModelV2) => {
                setOrder((old) => {
                  const services = old.services.filter(
                    (v) => v.item_id !== it.item_id,
                  );
                  return {
                    ...old,
                    services,
                    subtotal: calculateTotalSubtotal({
                      products: order.products,
                      services,
                      raw_materials: order.raw_materials,
                    }),
                  };
                });
                setFormId(Math.random());
              }}
              onSubmit={(data) => {
                setOrder((old) => {
                  const services = old.services.map((it) =>
                    it.id === item.id ? data : it,
                  );
                  return {
                    ...old,
                    services,
                    subtotal: calculateTotalSubtotal({
                      products: order.products,
                      services,
                      raw_materials: order.raw_materials,
                    }),
                  };
                });
                setFormId(Math.random());
              }}
            />
          ))}
          <ProductRowWidgetV2
            key={formId}
            id_prefix="order-create-service"
            edit={true}
            item_search_type={"Service"}
            onSubmit={(data) => {
              data.id = Date.now();
              setOrder((old) => {
                const alreadyExists = old.services.some(
                  (it) => it.item_id === data.item_id,
                );
                if (alreadyExists) {
                  showToast({
                    type: ToastType.error,
                    text: "Item already exists",
                  });
                  return old;
                }
                const services = [...old.services, data];
                return {
                  ...old,
                  services,
                  subtotal: calculateTotalSubtotal({
                    products: order.products,
                    services,
                    raw_materials: order.raw_materials,
                  }),
                };
              });
              setFormId(Math.random());
            }}
          />
          <div className="flex flex-col md:flex-row md:justify-between gap-1">
            <p className="text-xs md:text-sm italic text-gray-400">
              Please select product and qty then hit + to add product.
            </p>
            <p className="text-xs md:text-sm italic text-gray-400">
              Note: Enter the base price (excluding GST) — GST is applied
              automatically from the item's tax slab, and the final
              GST-inclusive price is what's used for the order.
            </p>
          </div>
        </div>
      </section>

      {/* Raw Materials */}
      <section
        id="order-create-raw-materials-section"
        className="overflow-x-auto"
      >
        {renderSectionHeader("Raw Materials", "raw")}
        <div className="hidden md:block bg-white rounded-md shadow p-3 my-2 text-sm">
          <div className="grid grid-cols-10 gap-3 ">
            <h4 className="font-semibold text-center">
              <span className="text-base text-red-500">*</span> Item Code
            </h4>
            <h4 className="font-semibold text-center">
              <span className="text-base text-red-500">*</span> Product/Service
            </h4>
            <h4 className="font-semibold text-center">
              <span className="text-base text-red-500">*</span> Description
            </h4>
            <h4 className="font-semibold text-center">
              <span className="text-base text-red-500">*</span> Unit
            </h4>
            <h4 className="font-semibold text-center">
              <span className="text-base text-red-500">*</span> Quantity
            </h4>
            <h4 className="font-semibold text-center">
              Price per unit (excl. GST)
            </h4>
            <h4 className="font-semibold text-center">GST %</h4>
            <h4 className="font-semibold text-center">
              Final Price (incl. GST)
            </h4>
            <h4 className="font-semibold text-center">Amount</h4>
            <h4 className="font-semibold text-center">Action</h4>
          </div>
        </div>
        <div className="flex flex-col gap-2 text-sm">
          {(order.raw_materials ?? []).map((item) => (
            <Fragment key={item.item_id}>
              <RawMaterialRowWidget
                id_prefix="order-create-raw-material"
                edit={false}
                item={item}
                item_search_type="Product"
                item_search_exclude_subcategory={FINISHED_GOODS_SUBCATEGORIES}
                availability={renderAvailability(item?.item?.item_code)}
                onDelete={(it: OrderItemModelV2) => {
                  setOrder((old) => {
                    const raw_materials = old.raw_materials.filter(
                      (v) => v.id !== it.id,
                    );
                    return {
                      ...old,
                      raw_materials,
                      subtotal: calculateTotalSubtotal({
                        products: order.products,
                        services: order.services,
                        raw_materials: raw_materials,
                      }),
                    };
                  });
                  setFormId(Math.random());
                }}
                onSubmit={(data) => {
                  setOrder((old) => {
                    const raw_materials = old.raw_materials.map((it) =>
                      it.id === item.id ? data : it,
                    );
                    return {
                      ...old,
                      raw_materials,
                      subtotal: calculateTotalSubtotal({
                        products: order.products,
                        services: order.services,
                        raw_materials: raw_materials,
                      }),
                    };
                  });
                  setFormId(Math.random());
                }}
              />
            </Fragment>
          ))}
          <RawMaterialRowWidget
            key={formId}
            id_prefix="order-create-raw-material"
            edit={true}
            item_search_type="Product"
            item_search_exclude_subcategory={FINISHED_GOODS_SUBCATEGORIES}
            availability={
              pendingRmItemCode ? renderAvailability(pendingRmItemCode) : null
            }
            onItemChange={(it) => {
              setPendingRmItemCode(it?.item_code ?? "");
              fetchAvailabilityForItem(it);
            }}
            onSubmit={(data) => {
              data.id = Date.now();
              setOrder((old) => {
                const alreadyExists = (old.raw_materials ?? []).some(
                  (it) => it.item_id === data.item_id,
                );
                if (alreadyExists) {
                  showToast({
                    type: ToastType.error,
                    text: "Item already exists",
                  });
                  return old;
                }
                const raw_materials = [...old.raw_materials, data];
                return {
                  ...old,
                  raw_materials,
                  subtotal: calculateTotalSubtotal({
                    products: order.products,
                    services: order.services,
                    raw_materials: raw_materials,
                  }),
                };
              });
              if (data?.item?.item_code) {
                fetchAvailabilityForItem(data.item);
              }
              setPendingRmItemCode("");
              setFormId(Math.random());
            }}
          />
        </div>
        <div className="flex flex-col md:flex-row md:justify-between gap-1">
          <p className="text-xs md:text-sm italic text-gray-400">
            Please select product and qty then hit + to add product.
          </p>
          <p className="text-xs md:text-sm italic text-gray-400">
            Note: Enter the base price (excluding GST) — GST is applied
            automatically from the item's tax slab, and the final GST-inclusive
            price is what's used for the order.
          </p>
        </div>
      </section>

      {bulkModal && (
        <ItemBulkUpload
          id_prefix={`order-create-${bulkModal}-bulk`}
          title={bulkModalTitle}
          validateRow={makeValidateRow(bulkModal)}
          onComplete={(items) => handleBulkComplete(bulkModal, items)}
          onClose={() => setBulkModal(null)}
        />
      )}
    </>
  );
};

export default ProductSection;
